import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  DollarSign, 
  Truck, 
  CheckCircle, 
  Star, 
  MessageCircle, 
  Recycle,
  Leaf,
  RefreshCw,
  Timer,
  Users,
  Award,
  Facebook,
  Shield,
  ChevronRight
} from 'lucide-react';
import Header from './components/Header';
import AnimatedSection from './components/AnimatedSection';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';
import CoverageNotice from './components/CoverageNotice';
import CTAButton from './components/CTAButton';
import FacebookModal from './components/FacebookModal';
import { useCountUp } from './hooks/useCountUp';

const testimonials = [
  { 
    id: 1, 
    name: 'Juan Pérez', 
    location: 'CDMX',
    date: 'hace 2 semanas',
    text: 'Neta que buen servicio, súper rápidos y me dieron el mejor precio. No batallé nada con el proceso.',
    car: 'Volkswagen Jetta 2012',
    payment: '$950'
  },
  { 
    id: 2, 
    name: 'Roberto Méndez', 
    location: 'CDMX',
    date: 'hace 1 mes',
    text: 'Como dueño de un taller mecánico, valoro mucho la eficiencia y seriedad del servicio. El proceso es transparente y los pagos son puntuales.',
    car: 'Nissan Sentra 2015',
    payment: '$1,100'
  },
  { 
    id: 3, 
    name: 'Carlos Rodríguez', 
    location: 'CDMX',
    date: 'hace 3 días',
    text: 'Qué chido que exista este servicio. Me ayudaron con la recolección a domicilio y todo súper bien.',
    car: 'Honda Civic 2014',
    payment: '$1,050'
  },
  {
    id: 4,
    name: 'Miguel Ángel Torres',
    location: 'Estado de México',
    date: 'hace 1 semana',
    text: 'Excelente atención, vinieron hasta mi casa en Ecatepec y me pagaron al momento. Muy recomendable.',
    car: 'Toyota Corolla 2013',
    payment: '$980'
  },
  {
    id: 5,
    name: 'Ana Laura Sánchez',
    location: 'Estado de México',
    date: 'hace 4 días',
    text: 'La mejor opción para vender el catalizador. El proceso fue muy rápido y me dieron un buen precio.',
    car: 'Chevrolet Cruze 2011',
    payment: '$920'
  },
  {
    id: 6,
    name: 'Fernando Ramírez',
    location: 'CDMX',
    date: 'hace 5 días',
    text: 'Me sorprendió lo fácil que fue todo el proceso. En menos de 24 horas ya tenía mi pago. Muy profesionales.',
    car: 'Mazda 3 2010',
    payment: '$900'
  }
];

const FAQ = [
  { 
    question: '¿Cuánto tiempo tarda el proceso?', 
    answer: 'El proceso generalmente toma entre 24 a 48 horas desde que recibes tu cotización hasta que recibes tu pago.' 
  },
  { 
    question: '¿Cómo se determina el precio de mi catalizador?', 
    answer: 'El precio se determina basándose en el modelo específico del catalizador que deseas vender. Requerimos conocer específicamente el catalizador que deseas vender ya que existen muchas opciones en el mercado.\nPor eso te pedimos que completes nuestro formulario de cotización para que podamos ofrecerte el mejor precio posible.' 
  },
  { 
    question: '¿Ofrecen servicio en toda la República Mexicana?', 
    answer: 'Por el momento solo contamos con servicio de recogida a domicilio en CDMX y algunos municipios del Estado de México. Sin embargo, podemos recibir tu catalizador por paquetería desde cualquier lugar de la República Mexicana. Tu pago está 100% garantizado.' 
  },
  { 
    question: '¿Es legal vender mi catalizador usado?', 
    answer: 'Sí, es completamente legal vender tu catalizador usado. Nosotros nos encargamos de su reciclaje responsable.' 
  },
  { 
    question: '¿Cómo se realiza el pago?', 
    answer: 'Ofrecemos varias opciones de pago, incluyendo transferencia bancaria y efectivo. Puedes elegir la que más te convenga.' 
  },
  {
    question: '¿Qué documentación necesito para vender mi catalizador?',
    answer: 'Para vender tu catalizador solo necesitas una identificación oficial vigente. No es necesario presentar documentación del vehículo.'
  },
  {
    question: '¿Puedo vender varios catalizadores al mismo tiempo?',
    answer: 'Sí, aceptamos múltiples catalizadores en una sola transacción. Cada uno será evaluado individualmente para ofrecerte el mejor precio.'
  },
  {
    question: '¿Qué marcas de catalizadores compran?',
    answer: 'Compramos catalizadores de todas las marcas y modelos de vehículos. Esto incluye automóviles, camionetas, motocicletas y vehículos comerciales.'
  },
  {
    question: '¿El catalizador debe estar en buen estado?',
    answer: 'Compramos catalizadores en cualquier estado. Sin embargo, el precio puede variar dependiendo de su condición y contenido de metales preciosos.'
  }
];

const sections = ['inicio', 'como-funciona', 'impacto-ambiental', 'por-que-elegirnos', 'testimonios', 'preguntas-frecuentes'];

export default function VendeTuCatalizadorApp() {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [activeSection, setActiveSection] = useState('inicio');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const catalCount = useCountUp('1000', 2500);
  const hoursCount = useCountUp(24, 2000);
  const satisfactionCount = useCountUp(100, 2000);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { top } = element.getBoundingClientRect();
          const elementPosition = window.scrollY + top;
          
          if (scrollPosition >= elementPosition && scrollPosition <= elementPosition + element.offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const event = new CustomEvent('sectionChange', { detail: activeSection });
    window.dispatchEvent(event);
  }, [activeSection]);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-blue-50 text-gray-800 relative">
      <FacebookModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <WhatsAppButton />
      <ScrollToTop />
      <Header activeSection={activeSection} />

      <main className="overflow-hidden">
        {/* Hero Section */}
        <section id="inicio" className="bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 text-white min-h-screen flex flex-col justify-between pt-28 md:pt-32">
          <div className="flex-grow flex items-center">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <AnimatedSection animation="fade-in-up" className="w-full md:w-1/2 text-center md:text-left mb-8 md:mb-0">
                  <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full uppercase tracking-wide mb-4">
                    VENDETUCATALIZADOR.COM.MX
                  </span>
                  <h1 className="text-3xl md:text-5xl font-bold mb-6">
                    <span className="block">Vende tu Catalizador</span>
                    <span className="block text-blue-200">Rápidamente y al Mejor Precio</span>
                  </h1>
                  <CTAButton onClick={() => setIsModalOpen(true)} />
                  <div className="flex flex-col items-center md:items-start gap-2 mt-3">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        <Shield size={14} className="text-white/90" />
                        <p className="text-white/90 text-xs">Pago 100% Seguro</p>
                      </div>
                      <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-white/90 text-xs">🇲🇽 Solo CDMX y Área Metropolitana</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        <Truck size={14} className="text-white/90" />
                        <p className="text-white/90 text-xs">Recolección a Domicilio</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => {
                        const element = document.getElementById('testimonios');
                        const headerOffset = 80;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }}
                      className="text-white/70 text-xs hover:text-white flex items-center gap-1 transition-all group mt-1"
                    >
                      <span className="border-b border-white/30 group-hover:border-white">Ver testimonios de clientes satisfechos</span>
                      <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </AnimatedSection>
                
                <AnimatedSection animation="fade-in" delay={300} className="w-full md:w-1/2 flex justify-center md:justify-end relative">
                  <div className="absolute inset-0 bg-gradient-radial from-blue-400/20 to-transparent rounded-full blur-3xl transform -translate-x-1/2"></div>
                  <div className="absolute inset-0 bg-gradient-radial from-blue-300/10 to-transparent rounded-full blur-2xl translate-x-1/4"></div>
                  
                  <img 
                    src={process.env.PUBLIC_URL + '/images/subhero.png'} 
                    alt="Representación de servicio rápido y mejor precio" 
                    className="max-w-full h-auto relative z-10 md:w-full md:max-w-none md:scale-150 md:translate-x-16 lg:scale-150 lg:translate-x-24"
                  />
                </AnimatedSection>
              </div>
            </div>
          </div>

          <div className="text-center pb-8 mt-8 md:mt-0">
            <p className="mb-2">Checa cómo lo hacemos</p>
            <ChevronDown className="mx-auto animate-bounce" size={24} />
          </div>
        </section>

        {/* Stats and How it Works Section */}
        <section id="como-funciona" className="py-12 bg-white/80 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Award size={40} />, number: catalCount, text: "Catalizadores Reciclados" },
                { icon: <Timer size={40} />, number: `${hoursCount}h`, text: "Tiempo Promedio de Pago" },
                { icon: <Users size={40} />, number: `${satisfactionCount}%`, text: "Clientes Satisfechos" }
              ].map((stat, index) => (
                <AnimatedSection key={index} delay={index * 200}>
                  <div className="text-center p-6 rounded-lg bg-gradient-to-br from-blue-50 to-white shadow-lg">
                    <div className="text-blue-500 mb-4">{stat.icon}</div>
                    <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                    <div className="text-gray-600">{stat.text}</div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <div className="mt-20">
              <AnimatedSection>
                <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">¿Cómo Funciona?</h2>
              </AnimatedSection>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { icon: <ChevronDown size={40} />, title: "Identifica el Código", description: "Usa nuestra guía visual para encontrar el código de tu catalizador." },
                  { icon: <MessageCircle size={40} />, title: "Completa el Formulario", description: "Ingresa detalles de tu catalizador y tu información de contacto." },
                  { icon: <Truck size={40} />, title: "Elige Método de Entrega", description: "Opta por recogida a domicilio o entrega en sucursal." },
                  { icon: <DollarSign size={40} />, title: "Tu Dinero en Mano", description: "Una vez verifiquemos tu catalizador, recibe tu dinero." },
                ].map((step, index) => (
                  <AnimatedSection key={index} delay={index * 200}>
                    <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center group hover:bg-gradient-to-br hover:from-blue-50 hover:to-white">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full p-4 inline-block mb-4 group-hover:scale-110 transition-transform duration-300">
                        {step.icon}
                      </div>
                      <h3 className="font-semibold text-xl mb-2 text-blue-700">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
              <div className="text-center mt-12">
                <CTAButton className="mt-8" variant="internal" onClick={() => setIsModalOpen(true)} />
              </div>
            </div>
          </div>
        </section>

        {/* Environmental Impact Section */}
        <section id="impacto-ambiental" className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <h2 className="text-4xl font-bold text-center mb-16">
                <span className="bg-gradient-to-r from-green-500 to-blue-400 bg-clip-text text-transparent inline-flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  Impacto Ambiental
                  <Leaf size={40} className="text-green-500 mt-2 sm:mt-0" strokeWidth={1.5} />
                </span>
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                {[
                  { icon: <Recycle size={24} />, title: "Reciclaje Responsable", description: "Cada catalizador reciclado contribuye a la reducción de la minería y la conservación de metales preciosos." },
                  { icon: <Leaf size={24} />, title: "Reducción de Residuos", description: "Evitamos que los catalizadores terminen en vertederos, dándoles una segunda vida útil." },
                  { icon: <RefreshCw size={24} />, title: "Economía Circular", description: "Fomentamos un ciclo sostenible de reutilización y reciclaje de materiales valiosos." }
                ].map((item, index) => (
                  <AnimatedSection key={index} delay={index * 200}>
                    <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:bg-gradient-to-br hover:from-green-50 hover:to-white">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-green-500">{item.icon}</div>
                        <h3 className="text-xl font-semibold text-green-600">{item.title}</h3>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
              <AnimatedSection animation="fade-in" delay={300}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-radial from-green-400/30 to-transparent rounded-full blur-3xl group-hover:scale-110 transition-transform duration-500"></div>
                  <div className="absolute inset-0 bg-gradient-radial from-blue-400/20 to-transparent rounded-full blur-2xl -translate-x-1/4 group-hover:translate-x-0 transition-transform duration-500"></div>
                  
                  <img 
                    src={process.env.PUBLIC_URL + '/images/CLAP.png'} 
                    alt="Impacto ambiental - Juntos por un futuro sostenible" 
                    className="relative z-10 transform hover:scale-105 transition-transform duration-300 w-full max-w-sm mx-auto filter drop-shadow-xl"
                  />
                  
                  <div className="absolute top-1/4 left-1/4 w-8 h-8 bg-green-400/30 rounded-full blur-sm animate-pulse"></div>
                  <div className="absolute bottom-1/4 right-1/4 w-6 h-6 bg-blue-400/30 rounded-full blur-sm animate-pulse delay-150"></div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section id="por-que-elegirnos" className="py-20 bg-gradient-to-b from-blue-50 to-white">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">¿Por Qué Elegirnos?</h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: <DollarSign size={30} />, title: "Cotizaciones Justas y Competitivas", description: "Ofrecemos los mejores precios del mercado por tu catalizador usado." },
                { icon: <CheckCircle size={30} />, title: "Proceso Rápido y Sencillo", description: "Nuestro sistema está diseñado para ahorrarte tiempo y esfuerzo." },
                { icon: <Star size={30} />, title: "Pago Seguro y Confiable", description: "Garantizamos transacciones seguras y transparentes." },
                { icon: <Truck size={30} />, title: "Opciones Flexibles de Entrega", description: "Adaptamos nuestro servicio a tu conveniencia." },
              ].map((reason, index) => (
                <AnimatedSection key={index} delay={index * 200}>
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-start group">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full p-2 mr-4 group-hover:scale-110 transition-transform duration-300">
                      {reason.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-blue-700">{reason.title}</h3>
                      <p className="text-gray-600">{reason.description}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <div className="text-center mt-12">
              <CTAButton className="mt-8" variant="internal" onClick={() => setIsModalOpen(true)} />
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonios" className="py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">Lo que dicen nuestros clientes</h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <AnimatedSection key={testimonial.id} delay={index * 200}>
                  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                    <div className="absolute top-0 right-0 -mt-2 -mr-2 bg-gradient-to-br from-yellow-400 to-yellow-500 text-white rounded-full p-2">
                      <Star size={16} />
                    </div>
                    <p className="mb-4 italic text-gray-600">"{testimonial.text}"</p>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between text-sm">
                        <p className="text-gray-500">Modelo: <span className="text-gray-700">{testimonial.car}</span></p>
                        <p className="text-gray-500">Pago: <span className="text-green-600 font-semibold">{testimonial.payment}</span></p>
                      </div>
                      <div className="flex items-center justify-between border-t pt-3">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                            {testimonial.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-blue-600">{testimonial.name}</p>
                            <p className="text-gray-500 text-sm">{testimonial.location}</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm">{testimonial.date}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <div className="text-center mt-12">
            <p className="text-gray-500 italic mb-8">Y más de 200 opiniones positivas de clientes satisfechos ...</p>
              <CTAButton className="mt-8" variant="internal" onClick={() => setIsModalOpen(true)} />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="preguntas-frecuentes" className="py-20 bg-gradient-to-b from-white to-blue-50">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <h2 className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">Preguntas Frecuentes</h2>
            </AnimatedSection>
            <div className="max-w-3xl mx-auto">
              {FAQ.map((item, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="mb-4">
                    <button
                      className="flex justify-between items-center w-full p-4 bg-white hover:bg-blue-50 rounded-lg focus:outline-none transition-colors duration-300 shadow-md hover:shadow-lg"
                      onClick={() => toggleAccordion(index)}
                    >
                      <span className="font-semibold text-blue-700">{item.question}</span>
                      <ChevronDown
                        className={`transform transition-transform ${
                          activeAccordion === index ? 'rotate-180' : ''
                        } text-blue-500`}
                      />
                    </button>
                    {activeAccordion === index && (
                      <div className="p-4 bg-blue-50 rounded-b-lg">
                        <p className="text-gray-600">{item.answer}</p>
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <div className="text-center mt-12">
              <CTAButton className="mt-8" variant="internal" onClick={() => setIsModalOpen(true)} />
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section id="cotizacion" className="py-4 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 text-white">
          <div className="container mx-auto text-center px-4">
            <AnimatedSection>
              <div className="flex justify-center mb-4 relative group">
                <div className="absolute inset-0 bg-gradient-radial from-blue-400/20 to-transparent rounded-full blur-3xl transform scale-150 group-hover:scale-175 transition-transform duration-500"></div>
                <div className="absolute inset-0 bg-gradient-radial from-blue-300/10 to-transparent rounded-full blur-2xl -translate-x-1/4 group-hover:translate-x-0 transition-transform duration-500"></div>
                
                <img 
                  src={process.env.PUBLIC_URL + '/images/CHOCALAS.png'} 
                  alt="Representación de servicio rápido y mejor precio" 
                  className="w-1/2 md:w-1/4 relative z-10 transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <span className="inline-block bg-blue-600 text-white text-xs px-3 py-1 rounded-full uppercase tracking-wide mb-4">
                VENDETUCATALIZADOR.COM.MX
              </span>
              <h2 className="text-4xl font-bold mb-8">Obtén tu Cotización Ahora</h2>
              <p className="mb-8 text-xl">Completa nuestro formulario y recibe tu cotización en tu WhatsApp lo más pronto posible.</p>
              <CTAButton onClick={() => setIsModalOpen(true)} />
              <CoverageNotice />
            </AnimatedSection>
          </div>
        </section>
      </main>

      <footer className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* About Section */}
            <AnimatedSection>
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <img 
                    src={process.env.PUBLIC_URL + '/images/favicon_catalizador.png'} 
                    alt="Logo VendeTuCatalizador" 
                    className="h-8 w-8 rounded-full shadow-md flex-shrink-0" 
                  />
                  <h5 className="text-sm md:text-base lg:text-base font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent leading-tight">
                    VendeTuCatalizador.com.mx
                  </h5>
                </div>
                <p className="text-blue-200 text-sm leading-relaxed">
                  La plataforma líder en México para la compra de catalizadores usados. 
                  Ofrecemos precios justos, procesos rápidos y un compromiso firme con el medio ambiente.
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                    <CheckCircle size={12} className="text-green-400" />
                    Pago Seguro
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                    <Truck size={12} className="text-blue-300" />
                    Recolección Gratis
                  </span>
                  <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                    <Leaf size={12} className="text-green-400" />
                    Eco-Friendly
                  </span>
                </div>
              </div>
            </AnimatedSection>

            {/* Quick Links */}
            <AnimatedSection delay={100}>
              <div>
                <h5 className="text-sm md:text-base lg:text-base font-bold mb-6 flex items-center gap-2">
                  <ChevronRight size={16} className="text-blue-300" />
                  Enlaces Rápidos
                </h5>
                <ul className="space-y-3">
                  {[
                    { text: 'Inicio', id: 'inicio' },
                    { text: 'Cómo Funciona', id: 'como-funciona' },
                    { text: 'Impacto Ambiental', id: 'impacto-ambiental' },
                    { text: 'Por Qué Elegirnos', id: 'por-que-elegirnos' },
                    { text: 'Testimonios', id: 'testimonios' },
                    { text: 'Preguntas Frecuentes', id: 'preguntas-frecuentes' }
                  ].map((link, index) => (
                    <li key={index}>
                      <button
                        onClick={() => {
                          const element = document.getElementById(link.id);
                          const headerOffset = 80;
                          const elementPosition = element.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                        }}
                        className="text-blue-200 hover:text-white transition-all duration-300 flex items-center gap-2 group text-sm"
                      >
                        <ChevronRight size={14} className="text-blue-400 group-hover:translate-x-1 transition-transform" />
                        {link.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>

            {/* Contact Info */}
            <AnimatedSection delay={200}>
              <div>
                <h5 className="text-sm md:text-base lg:text-base font-bold mb-6 flex items-center gap-2">
                  <MessageCircle size={16} className="text-blue-300" />
                  Contacto
                </h5>
                <div className="space-y-4">
                  <a 
                    href="mailto:soporte@vendetucatalizador.com.mx" 
                    className="flex items-start gap-3 text-blue-200 hover:text-white transition-all duration-300 group"
                  >
                    <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg group-hover:bg-white/20 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-white mb-1">Email</p>
                      <p className="break-all">soporte@vendetucatalizador.com.mx</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://wa.me/525612684036?text=¡Hola!%20Me%20interesa%20vender%20mi%20catalizador,%20¿podrían%20ayudarme?" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-blue-200 hover:text-white transition-all duration-300 group"
                  >
                    <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg group-hover:bg-white/20 transition-colors">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-white mb-1">WhatsApp</p>
                      <p>+52 56 1268 4036</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-3 text-blue-200">
                    <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg">
                      <Timer size={16} />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-white mb-1">Horario</p>
                      <p>Lun - Vie: 9:00 AM - 6:00 PM</p>
                      <p>Sábado: 9:00 AM - 2:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Social & Trust */}
            <AnimatedSection delay={300}>
              <div>
                <h5 className="text-sm md:text-base lg:text-base font-bold mb-6 flex items-center gap-2">
                  <Star size={16} className="text-blue-300" />
                  Síguenos
                </h5>
                <div className="space-y-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61554258313707&mibextid=ZbWKwL" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 p-3 rounded-lg group"
                  >
                    <Facebook className="w-5 h-5 text-blue-300 group-hover:scale-110 transition-transform" />
                    <div className="text-sm">
                      <p className="font-medium">Facebook</p>
                      <p className="text-blue-200 text-xs">Síguenos para ofertas especiales</p>
                    </div>
                  </a>

                  <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10">
                    <p className="text-xs text-blue-200 mb-3">Confianza y Seguridad</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Shield size={14} className="text-green-400" />
                        <span className="text-blue-100">Transacciones Seguras</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Award size={14} className="text-yellow-400" />
                        <span className="text-blue-100">+1000 Clientes Satisfechos</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Leaf size={14} className="text-green-400" />
                        <span className="text-blue-100">Compromiso Ambiental</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-center md:text-left">
                <p className="text-blue-200 text-sm">
                  &copy; {new Date().getFullYear()} VendeTuCatalizador.com.mx
                </p>
                <p className="text-blue-300 text-xs mt-1">
                  Todos los derechos reservados | Hecho con 💙 en México
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <DollarSign size={16} />
                  Cotizar Ahora
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
